import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useSignupReward } from "@/hooks/useSignupReward";
import { MobileShell } from "@/components/MobileShell";
import { BottomTabs } from "@/components/BottomTabs";
import { BrandHeader } from "@/components/BrandHeader";
import { Button } from "@/components/ui/button";
import { formatMoney } from "@/lib/money";
import { ArrowDownLeft, ArrowUpRight, LogOut, Send as SendIcon, QrCode, ScanLine, HandCoins, History, Loader2, Settings as SettingsIcon, Copy, X, Clock, Gift, Store, Sparkles, Briefcase, PiggyBank, Users, Receipt, Flame, Megaphone } from "lucide-react";
import { HustleBadge, type HustleLevel } from "@/components/HustleBadge";
import { TierBadge, type Tier } from "@/components/TierBadge";
import { toast } from "sonner";
import { NotificationsBell } from "@/components/NotificationsBell";
import { OnboardingTour } from "@/components/OnboardingTour";
import { ShieldCheck, Scale } from "lucide-react";

interface Profile {
  display_name: string;
  cashtag: string;
  is_merchant?: boolean;
  hustle_score?: number;
  hustle_level?: HustleLevel;
  tier?: Tier;
}
interface Wallet {
  balance_cents: number;
  currency: string;
}
interface Tx {
  id: string;
  sender_id: string;
  receiver_id: string;
  amount_cents: number;
  note: string | null;
  created_at: string;
}
interface PendingRequest {
  id: string;
  amount_cents: number;
  note: string | null;
  created_at: string;
}

const WalletPage = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const reward = useSignupReward();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [txs, setTxs] = useState<Tx[]>([]);
  const [counterparts, setCounterparts] = useState<Record<string, Profile>>({});
  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) return;
    const [{ data: p }, { data: w }, { data: t }, { data: r }] = await Promise.all([
      supabase.from("profiles").select("display_name, cashtag, is_merchant, hustle_score, hustle_level, tier").eq("user_id", user.id).maybeSingle(),
      supabase.from("wallets").select("balance_cents, currency").eq("user_id", user.id).maybeSingle(),
      supabase.from("transactions").select("*").or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`).order("created_at", { ascending: false }).limit(5),
      supabase.from("money_requests").select("id, amount_cents, note, created_at").eq("requester_id", user.id).eq("status", "pending").order("created_at", { ascending: false }),
    ]);
    setProfile(p as Profile | null);
    setWallet(w as Wallet | null);
    const txList = (t as Tx[] | null) ?? [];
    setTxs(txList);
    setPendingRequests((r as PendingRequest[] | null) ?? []);

    const counterIds = Array.from(
      new Set(txList.map((tx) => (tx.sender_id === user.id ? tx.receiver_id : tx.sender_id)))
    );
    if (counterIds.length) {
      const { data: profs } = await supabase
        .from("profiles")
        .select("user_id, display_name, cashtag")
        .in("user_id", counterIds);
      const map: Record<string, Profile> = {};
      (profs ?? []).forEach((pp: any) => {
        map[pp.user_id] = { display_name: pp.display_name, cashtag: pp.cashtag };
      });
      setCounterparts(map);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const copyRequestLink = async (id: string) => {
    const url = `${window.location.origin}/r/${id}`;
    await navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  const cancelRequest = async (id: string) => {
    const { error } = await supabase
      .from("money_requests")
      .update({ status: "cancelled" })
      .eq("id", id);
    if (error) {
      toast.error(error.message);
      return;
    }
    setPendingRequests((prev) => prev.filter((r) => r.id !== id));
    toast.success("Request cancelled");
  };

  const handleSignOut = async () => {
    await signOut();
    toast.success("Signed out");
    navigate("/", { replace: true });
  };

  return (
    <MobileShell>
      <OnboardingTour />
      <BrandHeader
        right={
          <div className="flex items-center gap-1">
            <NotificationsBell />
            {profile?.is_merchant && (
              <Link
                to="/merchant"
                aria-label="Merchant mode"
                className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-white/10 transition-smooth"
              >
                <Store className="h-5 w-5" />
              </Link>
            )}
            <Link
              to="/settings"
              aria-label="Settings"
              className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-white/10 transition-smooth"
            >
              <SettingsIcon className="h-5 w-5" />
            </Link>
            <button
              onClick={handleSignOut}
              aria-label="Sign out"
              className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-white/10 transition-smooth"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        }
      >
        <div className="mt-6 text-center">
          <p className="text-sm opacity-80">
            Hi, {profile?.display_name ?? "Islander"} • <span className="font-semibold">${profile?.cashtag}</span>
          </p>
          {(profile?.hustle_level || profile?.tier) && (
            <div className="mt-2 flex justify-center items-center gap-2 flex-wrap">
              {profile?.hustle_level && (
                <Link to="/points" aria-label="View points activity" className="hover:opacity-90 transition-opacity">
                  <HustleBadge level={profile.hustle_level} score={profile.hustle_score} showScore />
                </Link>
              )}
              {profile?.tier && <TierBadge tier={profile.tier} />}
            </div>
          )}
          <p className="mt-3 text-xs uppercase tracking-wider opacity-70">Available balance</p>
          <p className="font-display text-5xl font-extrabold mt-1">
            {wallet ? formatMoney(wallet.balance_cents, wallet.currency) : "—"}
          </p>
        </div>
      </BrandHeader>

      <div className="px-5 -mt-5">
        <div className="grid grid-cols-4 gap-2 bg-background rounded-2xl shadow-card p-4">
          <ActionTile to="/send" icon={SendIcon} label="Send" tone="primary" />
          <ActionTile to="/scan" icon={ScanLine} label="Scan" tone="primary" />
          <ActionTile to="/request" icon={HandCoins} label="Request" tone="gold" />
          <ActionTile to="/receive" icon={QrCode} label="Receive" tone="muted" />
        </div>
      </div>

      <section className="px-5 mt-4 space-y-3">
        <Link
          to="/spin"
          className="flex items-center gap-3 rounded-2xl bg-gradient-to-r from-accent/25 via-primary/15 to-accent/25 border border-accent/30 p-4 hover:scale-[1.01] transition-transform"
        >
          <div className="h-11 w-11 rounded-xl bg-accent/30 grid place-items-center shrink-0">
            <Sparkles className="h-6 w-6 text-accent-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display font-bold text-sm">Spin to Win 🎡</p>
            <p className="text-xs text-muted-foreground">3 free spins daily — points & cash prizes</p>
          </div>
          <span className="text-xs font-semibold text-accent-foreground bg-accent/20 px-2 py-1 rounded-full">Free</span>
        </Link>
        <div className="grid grid-cols-2 gap-3">
          <Link
            to="/vaults"
            className="flex items-center gap-3 rounded-2xl bg-card border border-border p-4 hover:bg-muted/40 transition-colors"
          >
            <div className="h-11 w-11 rounded-xl bg-primary/15 grid place-items-center shrink-0">
              <PiggyBank className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold text-sm">Cash Vaults</p>
              <p className="text-xs text-muted-foreground">Lock & save</p>
            </div>
          </Link>
          <Link
            to="/discipline"
            className="flex items-center gap-3 rounded-2xl bg-card border border-border p-4 hover:bg-muted/40 transition-colors"
          >
            <div className="h-11 w-11 rounded-xl bg-accent/20 grid place-items-center shrink-0">
              <Flame className="h-6 w-6 text-accent-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold text-sm">Discipline</p>
              <p className="text-xs text-muted-foreground">Score & streak</p>
            </div>
          </Link>
          <Link
            to="/family"
            className="flex items-center gap-3 rounded-2xl bg-card border border-border p-4 hover:bg-muted/40 transition-colors"
          >
            <div className="h-11 w-11 rounded-xl bg-primary/15 grid place-items-center shrink-0">
              <Users className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold text-sm">Family</p>
              <p className="text-xs text-muted-foreground">Shared groups</p>
            </div>
          </Link>
          <Link
            to="/bills"
            className="flex items-center gap-3 rounded-2xl bg-card border border-border p-4 hover:bg-muted/40 transition-colors"
          >
            <div className="h-11 w-11 rounded-xl bg-primary/15 grid place-items-center shrink-0">
              <Receipt className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold text-sm">Split bills</p>
              <p className="text-xs text-muted-foreground">Share & settle</p>
            </div>
          </Link>
        </div>
        <Link
          to="/ads"
          className="flex items-center gap-3 rounded-2xl bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 border border-primary/20 p-4 hover:scale-[1.01] transition-transform"
        >
          <div className="h-11 w-11 rounded-xl bg-primary/20 grid place-items-center shrink-0">
            <Megaphone className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display font-bold text-sm">Promote your business</p>
            <p className="text-xs text-muted-foreground">Buy a spot in the Discover feed</p>
          </div>
          <span className="text-xs font-semibold text-muted-foreground">→</span>
        </Link>
        <Link
          to="/discover"
          className="flex items-center gap-3 rounded-2xl bg-card border border-border p-4 hover:bg-muted/40 transition-colors"
        >
          <div className="h-11 w-11 rounded-xl bg-primary/15 grid place-items-center shrink-0">
            <Store className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display font-bold text-sm">Discover vendors</p>
            <p className="text-xs text-muted-foreground">Browse merchants on Cash Isle</p>
          </div>
          <span className="text-xs font-semibold text-muted-foreground">→</span>
        </Link>
        <Link
          to="/isle-fund"
          className="flex items-center gap-3 rounded-2xl bg-card border border-border p-4 hover:bg-muted/40 transition-colors"
        >
          <div className="h-11 w-11 rounded-xl bg-primary/15 grid place-items-center shrink-0">
            <HandCoins className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display font-bold text-sm">Isle Fund</p>
            <p className="text-xs text-muted-foreground">Crowdfund dreams across the isle</p>
          </div>
          <span className="text-xs font-semibold text-muted-foreground">→</span>
        </Link>
        <Link
          to="/gigs"
          className="flex items-center gap-3 rounded-2xl bg-card border border-border p-4 hover:bg-muted/40 transition-colors"
        >
          <div className="h-11 w-11 rounded-xl bg-primary/15 grid place-items-center shrink-0">
            <Briefcase className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display font-bold text-sm">Gigs & job drops</p>
            <p className="text-xs text-muted-foreground">Earn or post work with escrow</p>
          </div>
          <span className="text-xs font-semibold text-muted-foreground">→</span>
        </Link>
        <div className="grid grid-cols-2 gap-3">
          <Link
            to="/my-gigs"
            className="flex items-center gap-3 rounded-2xl bg-card border border-border p-4 hover:bg-muted/40 transition-colors"
          >
            <div className="h-11 w-11 rounded-xl bg-accent/15 grid place-items-center shrink-0">
              <Clock className="h-6 w-6 text-accent" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold text-sm">My gigs</p>
              <p className="text-xs text-muted-foreground">Shifts & history</p>
            </div>
          </Link>
          <Link
            to="/business"
            className="flex items-center gap-3 rounded-2xl bg-card border border-border p-4 hover:bg-muted/40 transition-colors"
          >
            <div className="h-11 w-11 rounded-xl bg-primary/15 grid place-items-center shrink-0">
              <Store className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold text-sm">My jobs posted</p>
              <p className="text-xs text-muted-foreground">Applicants & active</p>
            </div>
          </Link>
        </div>
        <Link
          to="/verification"
          className="flex items-center gap-3 rounded-2xl bg-card border border-border p-4 hover:bg-muted/40 transition-colors"
        >
          <div className="h-11 w-11 rounded-xl bg-primary/15 grid place-items-center shrink-0">
            <ShieldCheck className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display font-bold text-sm">Trust & verification</p>
            <p className="text-xs text-muted-foreground">Level up your tier & raise payout limits</p>
          </div>
          <span className="text-xs font-semibold text-muted-foreground">→</span>
        </Link>
        <Link
          to="/disputes"
          className="flex items-center gap-3 rounded-2xl bg-card border border-border p-4 hover:bg-muted/40 transition-colors"
        >
          <div className="h-11 w-11 rounded-xl bg-primary/15 grid place-items-center shrink-0">
            <Scale className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display font-bold text-sm">My disputes</p>
            <p className="text-xs text-muted-foreground">Track open escrow disputes</p>
          </div>
          <span className="text-xs font-semibold text-muted-foreground">→</span>
        </Link>
        <Link
          to="/withdraw"
          className="flex items-center gap-3 rounded-2xl bg-card border border-border p-4 hover:bg-muted/40 transition-colors"
        >
          <div className="h-11 w-11 rounded-xl bg-primary/15 grid place-items-center shrink-0">
            <SendIcon className="h-6 w-6 text-primary rotate-180" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display font-bold text-sm">Withdraw to bank</p>
            <p className="text-xs text-muted-foreground">Cash out your balance</p>
          </div>
          <span className="text-xs font-semibold text-muted-foreground">→</span>
        </Link>
      </section>

      {reward.status === "pending" && (
        <section className="px-5 mt-5 animate-fade-in-up">
          <div className="rounded-2xl bg-gradient-to-br from-accent/20 to-primary/10 border border-accent/30 p-4 flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-accent/25 grid place-items-center text-accent-foreground shrink-0">
              <Gift className="h-6 w-6" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold text-sm">$5 waiting for you 🌴</p>
              <p className="text-xs text-muted-foreground">
                Send your first payment to unlock your reward.
              </p>
            </div>
            <Button asChild size="sm" variant="gold">
              <Link to="/send">Send</Link>
            </Button>
          </div>
        </section>
      )}

      {reward.status === "blocked" && (
        <section className="px-5 mt-5">
          <div className="rounded-2xl bg-muted/60 border border-border p-3 text-xs text-muted-foreground text-center">
            Signup reward unavailable on this device.
          </div>
        </section>
      )}

      {pendingRequests.length > 0 && (
        <section className="px-5 mt-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-lg flex items-center gap-2">
              <Clock className="h-4 w-4 text-accent" /> Pending requests
            </h2>
            <span className="text-xs text-muted-foreground">{pendingRequests.length}</span>
          </div>
          <ul className="space-y-2">
            {pendingRequests.map((r) => (
              <li key={r.id} className="bg-secondary/50 rounded-xl px-4 py-3 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full grid place-items-center bg-accent/15 text-accent-foreground">
                  <HandCoins className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold">{formatMoney(r.amount_cents)}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {r.note ? `"${r.note}" · ` : ""}{new Date(r.created_at).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => copyRequestLink(r.id)}
                  aria-label="Copy link"
                  className="h-9 w-9 grid place-items-center rounded-full hover:bg-background transition-smooth"
                >
                  <Copy className="h-4 w-4" />
                </button>
                <button
                  onClick={() => cancelRequest(r.id)}
                  aria-label="Cancel request"
                  className="h-9 w-9 grid place-items-center rounded-full text-destructive hover:bg-destructive/10 transition-smooth"
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="px-5 mt-7 flex-1">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-lg">Recent activity</h2>
          <Link to="/history" className="text-sm text-primary font-medium flex items-center gap-1">
            <History className="h-4 w-4" /> All
          </Link>
        </div>

        {loading ? (
          <div className="py-12 grid place-items-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : txs.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-8 text-center text-muted-foreground text-sm">
            No transactions yet. Tap <span className="text-primary font-medium">Send</span> to start.
          </div>
        ) : (
          <ul className="space-y-2">
            {txs.map((tx) => {
              const isOut = tx.sender_id === user?.id;
              const other = counterparts[isOut ? tx.receiver_id : tx.sender_id];
              return (
                <li key={tx.id} className="flex items-center gap-3 bg-secondary/50 rounded-xl px-4 py-3">
                  <div className={`h-10 w-10 rounded-full grid place-items-center ${isOut ? "bg-destructive/10 text-destructive" : "bg-success/10 text-success"}`}>
                    {isOut ? <ArrowUpRight className="h-5 w-5" /> : <ArrowDownLeft className="h-5 w-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{isOut ? "To" : "From"} {other?.display_name ?? "Unknown"}</p>
                    <p className="text-xs text-muted-foreground truncate">${other?.cashtag} · {new Date(tx.created_at).toLocaleDateString()}</p>
                  </div>
                  <p className={`font-display font-bold ${isOut ? "text-foreground" : "text-success"}`}>
                    {isOut ? "-" : "+"}{formatMoney(tx.amount_cents)}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <BottomTabs />
    </MobileShell>
  );
};

const ActionTile = ({
  to,
  icon: Icon,
  label,
  tone,
  disabled,
}: {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  tone: "primary" | "gold" | "muted";
  disabled?: boolean;
}) => {
  const toneClasses = {
    primary: "bg-primary/10 text-primary",
    gold: "bg-accent/15 text-accent-foreground",
    muted: "bg-muted text-muted-foreground",
  }[tone];

  const inner = (
    <div className="flex flex-col items-center gap-2 py-2">
      <div className={`h-12 w-12 rounded-2xl grid place-items-center ${toneClasses}`}>
        <Icon className="h-6 w-6" />
      </div>
      <span className="text-sm font-medium">{label}</span>
    </div>
  );

  if (disabled) {
    return <div className="opacity-50 cursor-not-allowed">{inner}</div>;
  }
  return (
    <Link to={to} className="hover:scale-[1.02] active:scale-[0.98] transition-smooth">
      {inner}
    </Link>
  );
};

export default WalletPage;
